// Keep updated with SubGroupUserMembership in cryptoview/models/group.py
import {
  ApiTags,
  BotType,
  LanguageNameSpaces,
  LongShort,
  MarginTypes,
  MarketplaceSubGroup,
  QueryBotType,
  StopType,
  TagName,
  TimeUnit,
} from "@shared/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import { Color } from "@shared/common/Themes/types";
import { ValueOf } from "@shared/types/utils";
import { TranslationKeys } from "@shared/common/i18n/types";
import invert from "lodash/invert.js";

export const BASE_SYMBOLS_ICONS_PATH = "icons/symbols/colored/";
export const BASE_EXCHANGES_ICONS_PATH = "icons/exchanges/";

export const TIME_RANGE_START = "start";
export const TIME_RANGE_1_HOUR = "1h";
export const TIME_RANGE_6_HOURS = "6h";
export const TIME_RANGE_12_HOURS = "12h";
export const TIME_RANGE_1_DAY = "1d";
export const TIME_RANGE_3_DAYS = "3d";
export const TIME_RANGE_1_WEEK = "1w";
export const TIME_RANGE_1_MONTHS = "1m";
export const TIME_RANGE_3_MONTHS = "3m";
export const TIME_RANGE_6_MONTHS = "6m";
export const TIME_RANGE_1_YEAR = "1y";
export const TIME_RANGES = [
  TIME_RANGE_START,
  TIME_RANGE_1_HOUR,
  TIME_RANGE_6_HOURS,
  TIME_RANGE_12_HOURS,
  TIME_RANGE_1_DAY,
  TIME_RANGE_3_DAYS,
  TIME_RANGE_1_WEEK,
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_3_MONTHS,
  TIME_RANGE_6_MONTHS,
  TIME_RANGE_1_YEAR,
];

export const BASE_CHART_TIME_RANGES = [
  TIME_RANGE_1_DAY,
  TIME_RANGE_3_DAYS,
  TIME_RANGE_1_WEEK,
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_3_MONTHS,
  TIME_RANGE_1_YEAR,
];

export const TRADING_VIEW_TIME_RANGES = [
  TIME_RANGE_1_HOUR,
  TIME_RANGE_1_DAY,
  TIME_RANGE_1_WEEK,
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_3_MONTHS,
  TIME_RANGE_1_YEAR,
];

export const TRADE_DRAWER_TIME_RANGES = [
  TIME_RANGE_START,
  ...TRADING_VIEW_TIME_RANGES,
];

export const DISPLAY_CURRENCIES = {
  BTC: "BTC",
  USD: "USD",
} as const;

export enum ICON_TYPES {
  EXCHANGE = "exchange",
  COIN = "coin",
  DISPLAY_CURRENCY = "displayCurrency",
}

export const AUTH_SLICE_PATH = "authentication";

// Breakpoints

export const SMALL_MOBILE = "450px";
export const MOBILE_SEARCH = "600px";

export const MARGIN_TYPE_TO_DISPLAY: Record<MarginTypes, string> = {
  [MarginTypes.ISOLATED]: "Isolated",
  [MarginTypes.CROSS]: "Cross",
};

export const LONG_SHORT_TO_DISPLAY: Record<LongShort, string> = {
  [LongShort.LONG]: "Long",
  [LongShort.SHORT]: "Short",
};

export const STOP_TYPE_TO_DISPLAY: Record<StopType, string> = {
  [StopType.MARKET]: "Market",
  [StopType.LIMIT]: "Limit",
};

export const HOT_TRADES_HELPER_DESCRIPTION = "tooltips.hot_trades";

export const ENTRY_ACTIVITY_IDS = [1, 6, 9];

export const TAKE_PROFIT_ACTIVITY_IDS = [2, 7, 10];

export const STOP_ACTIVITY_IDS = [3, 8, 11];
export const SECONDS_IN_MINUTE = 60;
export const SECONDS_IN_HOUR = SECONDS_IN_MINUTE * 60;
export const SECONDS_IN_DAY = SECONDS_IN_HOUR * 24;
export const SECONDS_IN_WEEK = SECONDS_IN_DAY * 7;
export const SECONDS_IN_MONTH = SECONDS_IN_DAY * 30;
export const SECONDS_IN_YEAR = SECONDS_IN_MONTH * 12;
export const MINUTES_IN_HOUR = 60;
export const HOURS_IN_DAY = 24;
export const DAYS_IN_YEAR = 365;
export const DAYS_IN_WEEK = 7;
export const MS_IN_MINUTE = SECONDS_IN_MINUTE * 1000;

export const TIME_RANGE_TO_SECONDS = {
  [TIME_RANGE_1_DAY]: SECONDS_IN_DAY,
  [TIME_RANGE_3_DAYS]: SECONDS_IN_DAY * 3,
  [TIME_RANGE_1_WEEK]: SECONDS_IN_WEEK,
  [TIME_RANGE_1_MONTHS]: SECONDS_IN_MONTH,
  [TIME_RANGE_3_MONTHS]: SECONDS_IN_MONTH * 3,
  [TIME_RANGE_1_YEAR]: SECONDS_IN_YEAR,
};

export const TIME_DURATION_SUFFIX = {
  MINUTE: "m",
  HOUR: "h",
  DAY: "D",
  WEEK: "W",
  MONTH: "M",
  YEAR: "Y",
};

export const ORDER_TYPE_TO_CONFIGURATION_ITEM = {
  [OrderTypes.ENTRY]: "Trailing Entry",
  [OrderTypes.TAKE_PROFIT]: "Trailing Take Profit",
  [OrderTypes.STOP]: "Trailing Stop",
};

export const ORDER_TYPE_TO_TITLE_DISPLAY: Record<
  OrderTypes,
  TranslationKeys<LanguageNameSpaces.TRADING_TERMINAL>
> = {
  [OrderTypes.ENTRY]: "orders.entry",
  [OrderTypes.TAKE_PROFIT]: "orders.take_profit",
  [OrderTypes.STOP]: "orders.stop",
};

export const INITIAL_DATA_MAX_TIME_SECONDS = 100000000 * 60;

// When updating this constant, make sure to also update derived arrays like MARGIN_EXCHANGES if needed
export const EXCHANGES = {
  BINANCE: "Binance",
  BINANCE_FUTURES: "Binance Futures",
  BINANCE_COIN_FUTURES: "Binance Coin-Futures",
  BYBIT_SPOT: "ByBit Spot",
  BYBIT_USDT: "ByBit USDT",
  BYBIT_INVERSE: "ByBit",
  BYBIT_SPOT_MARGIN: "ByBit Spot Margin",
  OKX: "OKX",
  OKX_FUTURES: "OKX Futures",
  OKX_INVERSE: "OKX Inverse",
  OKX_EU_SPOT: "OKX EU Spot",
  OKX_EU_FUTURES: "OKX EU Futures",
  BITMEX: "BitMEX",
  KUCOIN: "KuCoin",
  KUCOIN_FUTURES: "KuCoin Futures",
  KRAKEN_SPOT: "Kraken Spot",
  BITTREX: "Bittrex",
  HUOBI: "Huobi.pro",
  FTX: "FTX",
  FTX_FUTURES: "FTX Futures",
  BITGET_SPOT: "Bitget Spot",
  BITGET_FUTURES: "Bitget Futures",
  BITGET_INVERSE: "Bitget Inverse",
  COINBASE_SPOT: "Coinbase Advanced Spot",
  COINBASE_FUTURES: "Coinbase Advanced Futures",
  HYPERLIQUID_SPOT: "HyperLiquid Spot",
  HYPERLIQUID_FUTURES: "HyperLiquid Futures",
  BINGX_SPOT: "BingX Spot",
  BINGX_FUTURES: "BingX Futures",
  BLOFIN_FUTURES: "BloFin Futures",
  BLOFIN_INVERSE: "BloFin Inverse",
  MEXC_SPOT: "MEXC Spot",
  MEXC_FUTURES: "MEXC Futures",
} as const;

export const PRIMARY_EXCHANGES: Record<string, string> = {
  BINANCE: "Binance",
  BYBIT: "ByBit",
  BITMEX: "BitMEX",
  HUOBI: "Huobi.pro",
  KUCOIN: "KuCoin",
  OKX: "OKX",
  OKX_EU: "OKX EU",
  BITGET: "Bitget",
  COINBASE: "Coinbase",
  HYPERLIQUID: "HyperLiquid",
  BINGX: "BingX",
  BLOFIN: "BloFin",
  KRAKEN: "Kraken",
  MEXC: "MEXC",
} as const;

export const EXCHANGE_TO_PRIMARY_EXCHANGE: Record<string, string> = {
  [EXCHANGES.BINANCE]: PRIMARY_EXCHANGES.BINANCE,
  [EXCHANGES.BINANCE_FUTURES]: PRIMARY_EXCHANGES.BINANCE,
  [EXCHANGES.BINANCE_COIN_FUTURES]: PRIMARY_EXCHANGES.BINANCE,
  [EXCHANGES.BYBIT_SPOT]: PRIMARY_EXCHANGES.BYBIT,
  [EXCHANGES.BYBIT_USDT]: PRIMARY_EXCHANGES.BYBIT,
  [EXCHANGES.BYBIT_INVERSE]: PRIMARY_EXCHANGES.BYBIT,
  [EXCHANGES.BYBIT_SPOT_MARGIN]: PRIMARY_EXCHANGES.BYBIT,
  [EXCHANGES.OKX]: PRIMARY_EXCHANGES.OKX,
  [EXCHANGES.OKX_EU_SPOT]: PRIMARY_EXCHANGES.OKX_EU,
  [EXCHANGES.OKX_FUTURES]: PRIMARY_EXCHANGES.OKX,
  [EXCHANGES.OKX_EU_FUTURES]: PRIMARY_EXCHANGES.OKX_EU,
  [EXCHANGES.OKX_INVERSE]: PRIMARY_EXCHANGES.OKX,
  [EXCHANGES.BITMEX]: PRIMARY_EXCHANGES.BITMEX,
  [EXCHANGES.KUCOIN]: PRIMARY_EXCHANGES.KUCOIN,
  [EXCHANGES.KUCOIN_FUTURES]: PRIMARY_EXCHANGES.KUCOIN,
  [EXCHANGES.KRAKEN_SPOT]: PRIMARY_EXCHANGES.KRAKEN,
  [EXCHANGES.HUOBI]: PRIMARY_EXCHANGES.HUOBI,
  [EXCHANGES.BITGET_SPOT]: PRIMARY_EXCHANGES.BITGET,
  [EXCHANGES.BITGET_FUTURES]: PRIMARY_EXCHANGES.BITGET,
  [EXCHANGES.BITGET_INVERSE]: PRIMARY_EXCHANGES.BITGET,
  [EXCHANGES.COINBASE_SPOT]: PRIMARY_EXCHANGES.COINBASE,
  [EXCHANGES.COINBASE_FUTURES]: PRIMARY_EXCHANGES.COINBASE,
  [EXCHANGES.HYPERLIQUID_SPOT]: PRIMARY_EXCHANGES.HYPERLIQUID,
  [EXCHANGES.HYPERLIQUID_FUTURES]: PRIMARY_EXCHANGES.HYPERLIQUID,
  [EXCHANGES.BINGX_SPOT]: PRIMARY_EXCHANGES.BINGX,
  [EXCHANGES.BINGX_FUTURES]: PRIMARY_EXCHANGES.BINGX,
  [EXCHANGES.BLOFIN_FUTURES]: PRIMARY_EXCHANGES.BLOFIN,
  [EXCHANGES.BLOFIN_INVERSE]: PRIMARY_EXCHANGES.BLOFIN,
  [EXCHANGES.MEXC_SPOT]: PRIMARY_EXCHANGES.MEXC,
  [EXCHANGES.MEXC_FUTURES]: PRIMARY_EXCHANGES.MEXC,
};

export const MARGIN_EXCHANGES: Set<string> = new Set([
  EXCHANGES.BINANCE_FUTURES,
  EXCHANGES.BINANCE_COIN_FUTURES,
  EXCHANGES.BYBIT_USDT,
  EXCHANGES.BYBIT_INVERSE,
  EXCHANGES.BYBIT_SPOT_MARGIN,
  EXCHANGES.OKX_FUTURES,
  EXCHANGES.OKX_EU_FUTURES,
  EXCHANGES.OKX_INVERSE,
  EXCHANGES.BITMEX,
  EXCHANGES.KUCOIN_FUTURES,
  EXCHANGES.FTX_FUTURES,
  EXCHANGES.BITGET_FUTURES,
  EXCHANGES.COINBASE_FUTURES,
  EXCHANGES.HYPERLIQUID_FUTURES,
  EXCHANGES.BINGX_FUTURES,
  EXCHANGES.BITGET_INVERSE,
  EXCHANGES.BLOFIN_FUTURES,
  EXCHANGES.BLOFIN_INVERSE,
  EXCHANGES.MEXC_FUTURES,
]);

export const CONTRACTS_EXCHANGES = new Set<string>([
  EXCHANGES.BINANCE_COIN_FUTURES,
  EXCHANGES.BYBIT_INVERSE,
  EXCHANGES.BITMEX,
  EXCHANGES.OKX_INVERSE,
  EXCHANGES.BITGET_INVERSE,
  EXCHANGES.BLOFIN_INVERSE,
]);

export const BINANCE_EXCHANGES = new Set<string>([
  EXCHANGES.BINANCE,
  EXCHANGES.BINANCE_FUTURES,
  EXCHANGES.BINANCE_COIN_FUTURES,
]);

export const ASSET_AS_PAIR_EXCHANGES = new Set<string>([
  EXCHANGES.BYBIT_INVERSE,
  EXCHANGES.BINANCE_COIN_FUTURES,
  EXCHANGES.OKX_INVERSE,
  EXCHANGES.BITGET_INVERSE,
  EXCHANGES.BLOFIN_INVERSE,
]);

export const MAX_LEVERAGE_CROSS_EXCHANGES: string[] = [EXCHANGES.BITMEX];

export const QUICK_CONNECT_EXCHANGES = new Set<string>([
  PRIMARY_EXCHANGES.BINANCE,
  PRIMARY_EXCHANGES.BYBIT,
  PRIMARY_EXCHANGES.OKX,
  PRIMARY_EXCHANGES.OKX_EU,
  PRIMARY_EXCHANGES.BITGET,
  PRIMARY_EXCHANGES.KUCOIN,
  PRIMARY_EXCHANGES.BINGX,
  PRIMARY_EXCHANGES.MEXC,
]);

export const HEDGE_MODE_EXCHANGES = new Set<string>([
  EXCHANGES.BINANCE_FUTURES,
  EXCHANGES.BINANCE_COIN_FUTURES,
  EXCHANGES.BYBIT_USDT,
  EXCHANGES.BITGET_FUTURES,
  EXCHANGES.BINGX_FUTURES,
  EXCHANGES.OKX_FUTURES,
  EXCHANGES.OKX_EU_FUTURES,
  EXCHANGES.OKX_INVERSE,
  EXCHANGES.BITGET_INVERSE,
  EXCHANGES.BLOFIN_FUTURES,
  EXCHANGES.BLOFIN_INVERSE,
  EXCHANGES.MEXC_FUTURES,
  EXCHANGES.KUCOIN_FUTURES,
]);

export const SUPPORTED_WHITELIST_IP_EXCHANGES = new Set<string>([
  PRIMARY_EXCHANGES.BINANCE,
  PRIMARY_EXCHANGES.BINGX,
  PRIMARY_EXCHANGES.BITGET,
  PRIMARY_EXCHANGES.COINBASE,
  PRIMARY_EXCHANGES.HUOBI,
  PRIMARY_EXCHANGES.KRAKEN,
  PRIMARY_EXCHANGES.MEXC,
]);

export const SPACE_SEPARATED_WHITELIST_IPS_EXCHANGES = new Set<string>([
  PRIMARY_EXCHANGES.BINANCE,
]);

export const DOLLAR_COINS = [
  "USD",
  "USDT",
  "BUSD",
  "TUSD",
  "USDC",
  "DAI",
  "USDP",
];

export const ENTRY_UUID: TranslationKeys<LanguageNameSpaces.BOTS> =
  "webhooks.create_trade";
export const SIGNAL_ENTRY_UUID: TranslationKeys<LanguageNameSpaces.BOTS> =
  "webhooks.create_signal";
export const TAKE_PROFIT_UUID: TranslationKeys<LanguageNameSpaces.BOTS> =
  "webhooks.close_trade";
export const SIGNAL_TAKE_PROFIT_UUID: TranslationKeys<LanguageNameSpaces.BOTS> =
  "webhooks.close_signal";

export const VIRTUAL_CARDS_LIST_PADDING = 20;

export const REGISTERED_EMAIL = "registered_email";
export const EMAIL_UUID_QUERY_PARAM = "email-uuid";
export const EXTERNAL_ACCOUNT_INVITE_CLIENT_TOKEN_QUERY_PARAM = "client_token";
export const SUPPORT_BOT_LINK = "https://t.me/cornix_tech_support_bot";
export const LOGIN_UNIQUE_ID_STORAGE_KEY = "login_unique_id";
export const EXTERNAL_INVITE_LOGIN_STORAGE_KEY = "is_external_invite_login";

export const IS_NUMERIC_REGEX = /^-?\d+$/;
export const SHORT_UUID_REGEX = /^[0-9A-F]{8}$/;
export const UUID_IN_USE_CODE = 409;
export const LOCAL_STORAGE_REGISTRATION_DATA_KEY = "registration_data";
export const STORAGE_OAUTH_DATA_KEY = "oauth_data";
export const STORAGE_OAUTH_TEMP_DATA_KEY = "oauth_temp_data";

export const BINANCE_FUTURES_LINK = "https://www.binance.com/en/futures";
export const BINANCE_API_MANAGEMENT_LINK =
  "https://www.binance.com/en/my/settings/api-management";
export const OKX_API_MANAGEMENT_LINK = "https://www.okx.com/account/my-api";
export const OKX_EU_API_MANAGEMENT_LINK = "https://my.okx.com/account/my-api";
export const MEXC_API_MANAGEMENT_LINK = "https://www.mexc.com/user/openapi";
export const BYBIT_API_MANAGEMENT_LINK =
  "https://www.bybit.com/app/user/api-management";
export const KUCOIN_API_MANAGEMENT_LINK = "https://www.kucoin.com/account/api";

export const ONBOARDING_STEP_TO_TITLE: Record<
  string,
  TranslationKeys<LanguageNameSpaces.COMMON>
> = {
  [OnboardingDetailedStep.SELECT_EXCHANGE]:
    "page_title.onboarding.select_exchange",
  [OnboardingDetailedStep.CONNECT_EXCHANGE]:
    "page_title.onboarding.connect_your_exchange",
  [OnboardingDetailedStep.TRADING_METHOD]:
    "page_title.onboarding.trading_method",
  [OnboardingDetailedStep.COPY_DCA_BOT]:
    "page_title.onboarding.method_settings",
  [OnboardingDetailedStep.DCA_BOT_OVERVIEW]: "page_title.onboarding.overview",
  [OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP]:
    "page_title.onboarding.method_settings",
  [OnboardingDetailedStep.SIGNALS_GROUP_OVERVIEW]:
    "page_title.onboarding.overview",
  [OnboardingDetailedStep.CONNECT_EXTERNAL_ACCOUNT]:
    "page_title.onboarding.method_settings",
};
export const PASSWORD_REGEX =
  /^(?=.*\d)(?=.*[!@#$%^&*()\-=+\[\]{}<>_?~;:,.\\/"'])(?=.*[a-z])(?=.*[A-Z]).{8,99}$/;
export const EIGHT_SYMBOLS_REGEX = /^.{8,}$/;
export const SPECIAL_SYMBOL_REGEX = /[!@#$%^&*()\-=+\[\]{}<>_?~;:,.\\"']+/;
export const UPPER_CASE_REGEX = /^(?=.*[A-Z]).*$/;
export const LOWER_CASE_REGEX = /^(?=.*[a-z]).*$/;
export const NUMBER_REGEX = /[0-9]+/;

export const INTERCOM_ARTICLE_ID = /\/(\d+)[^d]/;
export const UUID_ALREADY_CONNECTED_ARTICLE_ID = "6175288";

export const TERMS_OF_USE_LINK = "https://cornix.io/terms";
export const ASSET_MANAGER_TERMS_OF_USE_LINK =
  "https://cornix.io/asset-manager-community-admin-addendum";
export const AFFILIATION_AGREEMENT_TERMS_LINK =
  "https://cornix.io/affiliation_terms";
export const PRIVACY_POLICY_LINK = "https://cornix.io/privacy";

export const EMPTY_GROUPS_TITLE = "empty_states.no_groups_title";
export const EMPTY_GROUPS_DESCRIPTION = "empty_states.no_groups_description";

export const SIGNAL_URL_KEY = "signal";
export const SUB_GROUP_URL_KEY = "sub_group";
export const REGISTRATION_REF_URL_KEY = "ref";
export const SIGNAL_TARGET_URL_KEY = "target";
export const SIGNAL_TARGET_AUTO_TRADING = "auto_trading";

export const BASE_QUICK_CONNECT_PATH = "/auth";
export const BINANCE_AUTH_PATH = BASE_QUICK_CONNECT_PATH + "/binance";
export const BYBIT_AUTH_PATH = BASE_QUICK_CONNECT_PATH + "/bybit";
export const OKX_AUTH_PATH = BASE_QUICK_CONNECT_PATH + "/okx";
export const OKX_EU_AUTH_PATH = BASE_QUICK_CONNECT_PATH + "/okx-eu";
export const BITGET_AUTH_PATH = BASE_QUICK_CONNECT_PATH + "/bitget";
export const KUCOIN_AUTH_PATH = BASE_QUICK_CONNECT_PATH + "/kucoin";
export const BINGX_AUTH_PATH = BASE_QUICK_CONNECT_PATH + "/bingx";
export const MEXC_AUTH_PATH = BASE_QUICK_CONNECT_PATH + "/mexc";

export const TagNameToColor: Record<TagName, Color> = {
  [TagName.TOP_SELLER]: "primaryBlue",
  [TagName.NEW]: "green",
  [TagName.SALE]: "gold",
  [TagName.FREE]: "pink",
  [TagName.FREE_TRIAL]: "purple",
};

export const TAG_NAME_TO_PROPERTY_NAME: Record<
  TagName,
  keyof MarketplaceSubGroup
> = {
  [TagName.NEW]: "is_new",
  [TagName.SALE]: "discount",
  [TagName.TOP_SELLER]: "is_best_seller",
  [TagName.FREE]: "is_free",
  [TagName.FREE_TRIAL]: "has_free_trial",
};

export const NEW_IP_LOGIN_TITLE = "New IP Address Detected";
export const NEW_IP_LOGIN_DESCRIPTION =
  "An email has been sent to your registered email. Please check your inbox and follow the instructions provided to complete the verification process.";

export const TV_CHART_ORDER_TYPE_TO_DISPLAY_NAME = {
  [OrderTypes.ENTRY]: "Entry",
  //Using a weird 'e' for the word 'take' - do not touch!!!
  [OrderTypes.TAKE_PROFIT]: "Takе-Profit",
  [OrderTypes.STOP]: "Stop",
};

export const ACCESS_TOKEN_STORAGE_KEY = "access_token";
export const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";
export const IS_MOBILE_WEBVIEW_WINDOW_KEY = "is_mobile_view";

const EXCHANGES_SORT_LIST: ValueOf<typeof EXCHANGES>[] = [
  EXCHANGES.BINANCE,
  EXCHANGES.BINANCE_FUTURES,
  EXCHANGES.BINANCE_COIN_FUTURES,
  EXCHANGES.BYBIT_SPOT,
  EXCHANGES.BYBIT_USDT,
  EXCHANGES.BYBIT_INVERSE,
  EXCHANGES.BYBIT_SPOT_MARGIN,
  EXCHANGES.MEXC_SPOT,
  EXCHANGES.MEXC_FUTURES,
  EXCHANGES.OKX,
  EXCHANGES.OKX_EU_SPOT,
  EXCHANGES.OKX_FUTURES,
  EXCHANGES.OKX_EU_FUTURES,
  EXCHANGES.OKX_INVERSE,
  EXCHANGES.BITGET_SPOT,
  EXCHANGES.BITGET_FUTURES,
  EXCHANGES.BITGET_INVERSE,
  EXCHANGES.KUCOIN,
  EXCHANGES.KUCOIN_FUTURES,
  EXCHANGES.KRAKEN_SPOT,
  EXCHANGES.BINGX_SPOT,
  EXCHANGES.BINGX_FUTURES,
  EXCHANGES.BLOFIN_FUTURES,
  EXCHANGES.BLOFIN_INVERSE,
  EXCHANGES.HYPERLIQUID_SPOT,
  EXCHANGES.HYPERLIQUID_FUTURES,
  EXCHANGES.COINBASE_SPOT,
  EXCHANGES.COINBASE_FUTURES,
  EXCHANGES.BITMEX,
  EXCHANGES.HUOBI,
  EXCHANGES.BITTREX,
  EXCHANGES.FTX,
  EXCHANGES.FTX_FUTURES,
];

export const EXCHANGES_ORDER: Record<
  ValueOf<typeof EXCHANGES>,
  number
> = Object.fromEntries(
  EXCHANGES_SORT_LIST.map((exchange, index) => [exchange, index])
) as Record<ValueOf<typeof EXCHANGES>, number>;

export const EXCHANGES_UNIQUE_IDS: {
  [key in ValueOf<typeof EXCHANGES>]?: number;
} = {
  [EXCHANGES.BYBIT_INVERSE]: 1,
  [EXCHANGES.BINANCE_FUTURES]: 2,
  [EXCHANGES.BINANCE_COIN_FUTURES]: 3,
  [EXCHANGES.BINANCE]: 4,
  [EXCHANGES.BITMEX]: 5,
  [EXCHANGES.KUCOIN]: 6,
  [EXCHANGES.BITTREX]: 7,
  [EXCHANGES.HUOBI]: 8,
  [EXCHANGES.FTX_FUTURES]: 11,
  [EXCHANGES.FTX]: 12,
  [EXCHANGES.BYBIT_USDT]: 13,
  [EXCHANGES.BYBIT_SPOT]: 15,
  [EXCHANGES.OKX]: 16,
  [EXCHANGES.OKX_FUTURES]: 17,
  [EXCHANGES.OKX_INVERSE]: 18,
  [EXCHANGES.KUCOIN_FUTURES]: 19,
  [EXCHANGES.BITGET_FUTURES]: 20,
  [EXCHANGES.BITGET_INVERSE]: 21,
  [EXCHANGES.BITGET_SPOT]: 22,
  [EXCHANGES.COINBASE_SPOT]: 23,
  [EXCHANGES.HYPERLIQUID_SPOT]: 24,
  [EXCHANGES.HYPERLIQUID_FUTURES]: 25,
  [EXCHANGES.BINGX_FUTURES]: 26,
  [EXCHANGES.BINGX_SPOT]: 27,
  [EXCHANGES.BYBIT_SPOT_MARGIN]: 28,
  [EXCHANGES.BLOFIN_FUTURES]: 29,
  [EXCHANGES.BLOFIN_INVERSE]: 30,
  [EXCHANGES.MEXC_FUTURES]: 31,
  [EXCHANGES.MEXC_SPOT]: 32,
  [EXCHANGES.KRAKEN_SPOT]: 33,
  [EXCHANGES.OKX_EU_SPOT]: 34,
  [EXCHANGES.OKX_EU_FUTURES]: 35,
};

export const SUBSCRIBE_GROUP_REFETCH_ENDPOINTS_LIST = [
  { type: ApiTags.GROUP_DATA, id: "LIST" },
  { type: ApiTags.CLIENTS, id: "LIST" },
  { type: ApiTags.GROUPS, id: "LIST" },
];

export const UNSAVED_CHANGES_WARNING_MESSAGE =
  "You have unsaved changes. Proceeding with this action will discard them unless you save your changes first.\nAre you sure you wish to proceed and discard your changes?";

export const LOCAL_STORAGE_UTM_DATA_KEY = "utm_data";

export const UUID_REGEX =
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

export let IS_NET_CONNECTED = true;
export const setIsOnline = (isOnline: boolean) => {
  IS_NET_CONNECTED = isOnline;
};

export const TIME_UNITS_ORDER = [
  TimeUnit.YEAR,
  TimeUnit.DAY,
  TimeUnit.HOUR,
  TimeUnit.MINUTE,
  TimeUnit.SECOND,
];

export const TIME_UNIT_TO_MULTIPLIER = {
  [TimeUnit.YEAR]: DAYS_IN_YEAR,
  [TimeUnit.DAY]: HOURS_IN_DAY,
  [TimeUnit.HOUR]: MINUTES_IN_HOUR,
  [TimeUnit.MINUTE]: SECONDS_IN_MINUTE,
  [TimeUnit.SECOND]: 0,
};

export const LIFETIME_DATE = "12/31/9999";
export const WORKSPACE_STORAGE_KEY = "workspace";
export const ENV_TYPE_QUERY_PARAM = "env-type";

export const BOT_TYPES = [
  BotType.SIGNAL,
  BotType.ASAP,
  BotType.GRID,
  BotType.TRADING_VIEW,
  BotType.TRADING_VIEW_SIGNALS,
  BotType.HEDGING,
];

export const QUERY_BOT_TYPE_TO_BOT_TYPE: Record<QueryBotType, BotType> = {
  [QueryBotType.ASAP]: BotType.ASAP,
  [QueryBotType.TRADING_VIEW]: BotType.TRADING_VIEW,
  [QueryBotType.SIGNAL]: BotType.SIGNAL,
  [QueryBotType.GRID]: BotType.GRID,
  [QueryBotType.TRADING_VIEW_SIGNALS]: BotType.TRADING_VIEW_SIGNALS,
  [QueryBotType.HEDGING]: BotType.HEDGING,
};

export const BOT_TYPE_TO_QUERY_BOT_TYPE = invert(
  QUERY_BOT_TYPE_TO_BOT_TYPE
) as Record<BotType, QueryBotType>;

export const LEVERAGE_EXCHANGES_TAG = "leverage_exchanges";
export const NON_LEVERAGE_EXCHANGES_TAG = "non_leverage_exchanges";
export const MAX_ACCOUNT_NAME_LENGTH = 50;

export const CHANGE_FULL_NAME_FEATURE_FLAG = "change_full_name";
export const AMOUNT_PERCENTAGE_BASE_FEATURE_FLAG = "amount_percentage_base";
export const MOBILE_BILLING_FEATURE_FLAG = "mobile_billing";
export const ARCHIVED_BOTS_FEATURE_FLAG = "archived_bots";
export const HEDGING_BOTS_FEATURE_FLAG = "hedging_bot";
export const MCP_FEATURE_FLAG = "mcp";
export const TIME_STOP_FEATURE_FLAG = "time_stop";
/** Shows the new design toggle inside the platform. */
export const NEW_DESIGN_FEATURE_FLAG = "new_design";
/** Keeps the platform in the new design, without the toggle back to the classic look. */
export const NEW_DESIGN_ONLY_FEATURE_FLAG = "new_design_only";

export const REQUESTS_AUTH_HEADER = "Authorization";
export const WHOP_REQUESTS_AUTH_HEADER = "X-Authorization";
export const USER_AGENT_HEADER = "User-Agent";

export const MOBILE_APP_USER_AGENT_VALUE = "mobile";
export const MOBILE_APP_USER_AGENT_KEY = "App-Type";

export const TRADE_EXTRA_DETAILS_TABS = Object.freeze({
  GENERAL: "General",
  TIMELINE: "Timeline",
  ORDERS: "Orders",
  GRIDS: "Grids",
  CONFIGURATION: "Configuration",
});

export const TRADE_EXTRA_DETAILS_TABS_TO_DISPLAY: Record<
  string,
  TranslationKeys<LanguageNameSpaces.TRADES>
> = {
  [TRADE_EXTRA_DETAILS_TABS.GENERAL]: "drawer_tabs.general",
  [TRADE_EXTRA_DETAILS_TABS.TIMELINE]: "drawer_tabs.timeline",
  [TRADE_EXTRA_DETAILS_TABS.ORDERS]: "drawer_tabs.orders",
  [TRADE_EXTRA_DETAILS_TABS.GRIDS]: "drawer_tabs.grids",
  [TRADE_EXTRA_DETAILS_TABS.CONFIGURATION]: "drawer_tabs.configuration",
};

export const SIGNAL_EXTRA_DETAILS_TABS = Object.freeze({
  TIMELINE: "Timeline",
  TARGETS: "Targets",
  GENERAL: "General",
  CONFIGURATION: "Configuration",
  TRADES: "Trades",
});

export const SIGNAL_EXTRA_DETAILS_TABS_TO_DISPLAY: Record<
  string,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  [SIGNAL_EXTRA_DETAILS_TABS.TIMELINE]: "extra_details.tabs.timeline",
  [SIGNAL_EXTRA_DETAILS_TABS.TARGETS]: "extra_details.tabs.targets",
  [SIGNAL_EXTRA_DETAILS_TABS.GENERAL]: "extra_details.tabs.general",
  [SIGNAL_EXTRA_DETAILS_TABS.CONFIGURATION]: "extra_details.tabs.configuration",
  [SIGNAL_EXTRA_DETAILS_TABS.TRADES]: "extra_details.tabs.trades",
};

export const MY_TV_SIGNALS_BOT_FILTERS_SLICE_NAME = "myTradingViewSignalsBots";
